import { createTimerRunningState } from './createTimerRunningState';
import { DISMISS_STATE_KIND } from './DISMISS_STATE_KIND';
import type { DismissStateTransition } from './DismissStateTransition';
import { getLocalState } from './getLocalState';
import { reportUnexpectedDismissState } from './reportUnexpectedDismissState';
import { setLocalState } from './setLocalState';
import { THIRTY_DAYS_MS } from './THIRTY_DAYS_MS';

export const recordSourcesClicked = async (aaid: string): Promise<DismissStateTransition> => {
	const state = getLocalState(aaid);
	const now = Date.now();

	if (state === null) {
		// Called before first view was recorded: write TIMER_RUNNING with both timestamps set to now.
		// This keeps icons visible as static and starts the 14-day Sources Dropdown window immediately.
		await setLocalState(aaid, createTimerRunningState(now, now));
		return { from: null, to: DISMISS_STATE_KIND.TIMER_RUNNING, noop: false };
	}

	switch (state.kind) {
		case DISMISS_STATE_KIND.DISMISSED:
			// Dismissed is final: opening the Sources Dropdown later should not make icons visible again.
			return {
				from: DISMISS_STATE_KIND.DISMISSED,
				to: DISMISS_STATE_KIND.DISMISSED,
				noop: true,
			};

		case DISMISS_STATE_KIND.TIMER_RUNNING:
			// Sources Dropdown was already opened before, so keep the original timer start time.
			return {
				from: DISMISS_STATE_KIND.TIMER_RUNNING,
				to: DISMISS_STATE_KIND.TIMER_RUNNING,
				noop: true,
			};

		case DISMISS_STATE_KIND.SEEN:
			// SEEN state past the 30-day safety net is effectively expired. Do not promote
			// to TIMER_RUNNING — that would re-surface icons that the render-mode query
			// already treats as hidden, undoing the implicit "user disengaged" signal.
			if (now - state.firstViewedAt >= THIRTY_DAYS_MS) {
				return { from: DISMISS_STATE_KIND.SEEN, to: DISMISS_STATE_KIND.SEEN, noop: true };
			}

			await setLocalState(aaid, createTimerRunningState(state.firstViewedAt, now));
			return {
				from: DISMISS_STATE_KIND.SEEN,
				to: DISMISS_STATE_KIND.TIMER_RUNNING,
				noop: false,
			};

		default:
			reportUnexpectedDismissState({
				functionName: 'recordSourcesClicked',
				kind: (state as { kind?: string }).kind,
			});
			return { from: null, to: null, noop: true };
	}
};
